/**
 * Error detection utilities for nonce-consuming errors
 * Matches Go terminal implementation patterns
 */

/**
 * Check if an error is a nonce-related error
 * These errors indicate the nonce was invalid, too low, already used, etc.
 */
export function isNonceError(error: any): boolean {
  if (!error) return false;

  const errorStr = String(error).toLowerCase();
  const errorMessage = error?.message?.toLowerCase() || errorStr;
  
  // Also check response body if available (from HTTP errors)
  const responseBody = error?.responseBody || error?.onboardResult;
  const responseError = responseBody?.error?.toLowerCase() || responseBody?.message?.toLowerCase() || '';

  const noncePatterns = [
    'nonce too low',
    'nonce too high',
    'nonce already used',
    'nonce already pending',
    'nonce invalid',
    'nonce expired',
    'nonce mismatch',
    'invalid nonce',
    'nonce out of order',
  ];

  const allErrorText = `${errorMessage} ${errorStr} ${responseError}`.toLowerCase();
  return noncePatterns.some(pattern => allErrorText.includes(pattern));
}

/**
 * Check if an error is a signature verification error
 * These errors consume nonces on the router, so we need to re-sync nonces after them
 */
export function isSignatureVerificationError(error: any): boolean {
  if (!error) return false;

  const errorStr = String(error).toLowerCase();
  const errorMessage = error?.message?.toLowerCase() || errorStr;
  
  // Also check response body if available (from HTTP errors)
  const responseBody = error?.responseBody || error?.onboardResult;
  const responseError = responseBody?.error?.toLowerCase() || responseBody?.message?.toLowerCase() || '';

  const sigPatterns = [
    'signature verification failed',
    'invalid signature',
    'unauthenticated',
    'signature mismatch',
    'signature invalid',
    // NOTE: "nonce validation failed" is too broad - it matches "nonce already pending"
    // which should be handled by incrementing, not clearing. So we don't include it here.
  ];

  const allErrorText = `${errorMessage} ${errorStr} ${responseError}`.toLowerCase();
  return sigPatterns.some(pattern => allErrorText.includes(pattern));
}

/**
 * Check if an error consumes nonces (either nonce errors or signature verification errors)
 * This is a combined check for errors that require nonce re-sync
 * 
 * @returns Tuple of [isNonceErr, isSigVerificationErr]
 */
export function isNonceConsumingError(error: any): [boolean, boolean] {
  if (!error) {
    return [false, false];
  }

  const isNonceErr = isNonceError(error);
  const isSigVerificationErr = isSignatureVerificationError(error);

  return [isNonceErr, isSigVerificationErr];
}

/**
 * Check if an error should trigger nonce refresh
 * Returns true if the error consumes nonces (nonce error OR signature verification error)
 */
export function shouldRefreshNonce(error: any): boolean {
  const [isNonceErr, isSigVerificationErr] = isNonceConsumingError(error);
  return isNonceErr || isSigVerificationErr;
}
